import { createActionCreators } from 'immer-reducer';

import { CombinedSickLeaves, SickLeave, SickLeavesReducer } from '@/store/reducers/sick-leaves';
import { getActions } from '@/store/actions/actions';
import { getVacationsCalendar } from '@/store/actions/profile';
import { revalidate } from '@/store/responses';
import { combineDays } from '@/utils/combineDays';
import { AsyncAction } from './common';

export const sickLeavesActions = createActionCreators(SickLeavesReducer);

export type SickLeavesActions =
  | ReturnType<typeof sickLeavesActions.setSickLeaves>
  | ReturnType<typeof sickLeavesActions.setPersonalSickLeaves>
  | ReturnType<typeof sickLeavesActions.cleanSickLeaves>;

export const combineSickLeaves = (sickLeaves: SickLeave[]) => {
  if (!sickLeaves.length) return [];
  const objectWithUsers: {
    [key: string]: {
      sickleaves: SickLeave[];
      combinedSL: SickLeave[][];
    };
  } = {};

  sickLeaves.forEach((sickLeave) => {
    if (!objectWithUsers[sickLeave.userId]) {
      objectWithUsers[sickLeave.userId] = {
        sickleaves: [sickLeave],
        combinedSL: [],
      };
      return;
    }
    objectWithUsers[sickLeave.userId].sickleaves.push(sickLeave);
  });

  Object.keys(objectWithUsers).forEach((key) => {
    objectWithUsers[key].sickleaves = objectWithUsers[key].sickleaves.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
    objectWithUsers[key].combinedSL = combineDays(
      objectWithUsers[key].sickleaves,
      (data) => data.date,
    ).newDates;
  });

  const combinedSickLeaves: CombinedSickLeaves[] = [];
  Object.keys(objectWithUsers).forEach((key) => {
    objectWithUsers[key].combinedSL.forEach((combinedArray) => {
      const length = combinedArray.length - 1;
      combinedSickLeaves.push({
        _id: combinedArray[0]._id,
        startDate: combinedArray[0].date,
        endDate: combinedArray[length].date,
        sickLeaves: [...combinedArray].reverse(),
        userId: +key,
      });
    });
  });
  combinedSickLeaves
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  return combinedSickLeaves;
};

export const getAllSickLeaves = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    const sickLeaves = await mainProtectedApi.getAllSickLeaves();

    const combinedSickLeaves = combineSickLeaves(sickLeaves);

    dispatch(sickLeavesActions.setSickLeaves(sickLeaves, combinedSickLeaves));
  } catch (e) {
    console.log(e);
  }
};

export const getUserSickLeaves = (userId: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      `/sick-leave/user/${userId}`,
      () => mainProtectedApi.getUserSickLeaves(userId),
      (sickLeaves) => {
        const combinedSickLeaves = combineSickLeaves(sickLeaves);

        dispatch(sickLeavesActions.setSickLeaves(sickLeaves, combinedSickLeaves));
      },
      // getVacationsCalendar writes this field too.
      { slot: 'sickLeaveReducer.sickLeaves' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const getPersonalSickLeaves = (): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await revalidate(
      '/sick-leave',
      () => mainProtectedApi.getPersonalSickLeaves(),
      (sickLeaves) => {
        const combinedSickLeaves = combineSickLeaves(sickLeaves);

        dispatch(sickLeavesActions.setPersonalSickLeaves(sickLeaves, combinedSickLeaves));
      },
      { slot: 'sickLeaveReducer.personalSickLeaves' },
    );
  } catch (e) {
    console.log(e);
  }
};

export const viewSickLeave = (sickLeaveId: number): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await mainProtectedApi.viewSickLeave(sickLeaveId);
    dispatch(getActions());
  } catch (e) {
    console.log(e);
  }
};

// Each screen renders a different slice of the store, filled by a different
// endpoint, so after a mutation the caller has to say which one to refetch --
// refreshing the wrong slice leaves the visible list untouched until reload.
interface SickLeavesTarget {
  userId?: number;
  isAction?: boolean;
  isCalendar?: boolean;
  onError?: () => void;
}

const refreshSickLeaves = (
  { userId, isAction, isCalendar }: SickLeavesTarget,
): AsyncAction => async (dispatch) => {
  if (isAction) {
    dispatch(getActions());
  } else if (isCalendar) {
    dispatch(getVacationsCalendar());
  } else if (userId) {
    dispatch(getUserSickLeaves(userId));
  } else {
    dispatch(getPersonalSickLeaves());
  }
};

export const takeSickLeave = (
  comment: string,
  target: SickLeavesTarget = {},
): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await mainProtectedApi.takeSickLeave({ comment });

    dispatch(refreshSickLeaves(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

export const deleteSickLeave = (
  sickLeaveId: number,
  target: SickLeavesTarget = {},
): AsyncAction => async (
  dispatch,
  _,
  { mainProtectedApi },
) => {
  try {
    await mainProtectedApi.deleteSickLeave(sickLeaveId);

    dispatch(refreshSickLeaves(target));
  } catch (e) {
    console.log(e);
    if (target.onError) target.onError();
  }
};

export const cleanSickLeaves = (): AsyncAction => async (
  dispatch,
) => {
  try {
    dispatch(sickLeavesActions.cleanSickLeaves());
  } catch (e) {
    console.log(e);
  }
};
